import React, { MutableRefObject, useEffect, useState, KeyboardEvent, ChangeEvent } from 'react';
import { useOnClickOutside } from 'utils/hooks';
import { EnumInputSize, EnumInputTheme, KeyCode } from 'constants/enums';
import AddressDropdownList from './AddressDropdownList';
import Input, { EnumInputErrorTypes } from 'Components/Input/Input';
import styled from 'styled-components';
import { IAddress, IAddressWithCoordinate } from 'Modules/SearchAddress/types';
import { ADDRESS_DROPDOWN_INPUT_TEST_ID, ADDRESS_DROPDOWN_TEST_ID } from 'Components/SearchAddress/constants';
import SearchHistory from '../SearchHistory';
import { useDispatch, useSelector } from 'react-redux';
import AddressActions from 'Modules/SearchAddress/actions';
import { isFirstVisitSearchAddress } from '../utils';
import { getCurrentPosition } from 'Modules/SearchAddress/actions';
import { getCurrentCityId } from 'Modules/SearchAddress/selectors';

interface IAddressDropdownProps {
  enteredAddress: string;
  list?: IAddress[];
  size?: EnumInputSize;
  use?: EnumInputTheme;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  errorMsg?: string | boolean;
  autoFocus?: boolean;
  onChangeAddress: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelectAddress: (value: IAddress) => void;
  onFocusInput?: () => void;
  before?: React.ReactChild | React.ReactNode;
  after?: React.ReactChild | React.ReactNode;
  submitBtnRef?: React.RefObject<HTMLButtonElement>;
}

const Container = styled.div`
  position: relative;
  z-index: 1;
`;

enum EnumStepAddressDropdown {
  history = 'history',
  list = 'list',
  empty = 'empty',
}

const AddressDropdown: React.FC<IAddressDropdownProps> = ({
  enteredAddress,
  size = EnumInputSize.large,
  use = EnumInputTheme.round,
  placeholder = 'Укажите адрес доставки',
  className,
  onChangeAddress,
  onSelectAddress,
  onFocusInput,
  list = [],
  errorMsg,
  autoFocus,
  after,
  before,
  submitBtnRef,
}: IAddressDropdownProps) => {
  const dispatch = useDispatch();
  const cityId = useSelector(getCurrentCityId);
  const containerEl: MutableRefObject<any> = React.useRef(null);
  const inputRef: MutableRefObject<any> = React.useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [stepAddressDropdown, setStepAddressDropdown] = useState<EnumStepAddressDropdown>(
    EnumStepAddressDropdown.empty
  );

  useEffect(() => {
    if (submitBtnRef?.current && list.length) {
      const listener = () => {
        onSelectAddress(list[hoveredIndex]);
      };
      const startListenSubmit = () => submitBtnRef.current?.addEventListener('click', listener);
      const stopListenSubmit = () => submitBtnRef.current?.removeEventListener('click', listener);

      startListenSubmit();

      return stopListenSubmit;
    }
  }, [hoveredIndex, list, onSelectAddress, submitBtnRef]);

  useOnClickOutside(containerEl, () => {
    setStepAddressDropdown(EnumStepAddressDropdown.empty);
  });

  useEffect(() => {
    const isInputFocused = inputRef?.current === document.activeElement;

    setStepAddressDropdown(
      list.length
        ? EnumStepAddressDropdown.list
        : !enteredAddress.length && isInputFocused
        ? EnumStepAddressDropdown.history
        : EnumStepAddressDropdown.empty
    );

    if (list.length) {
      setHoveredIndex(0);
    }
  }, [enteredAddress.length, list]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!list.length) return setHoveredIndex(-1);
    const { keyCode } = e;

    if (keyCode === KeyCode.Enter && hoveredIndex >= 0) {
      e.preventDefault();
      onSelectAddress(list[hoveredIndex]);
      setHoveredIndex(-1);
    } else if (keyCode === KeyCode.Down && hoveredIndex < list.length - 1) {
      setHoveredIndex(hoveredIndex + 1);
    } else if (keyCode === KeyCode.Up && hoveredIndex > 0) {
      e.preventDefault();
      setHoveredIndex(hoveredIndex - 1);
    }
  };

  const handleSelectAddress = (value: IAddress) => {
    onSelectAddress(value);
    inputRef?.current?.focus();
  };

  const handleFocus = () => {
    if (!enteredAddress && !list?.length) {
      setStepAddressDropdown(EnumStepAddressDropdown.history);
    }
    if (isFirstVisitSearchAddress()) {
      getCurrentPosition(dispatch, cityId);
    }
    onFocusInput && onFocusInput();
  };

  const handleSelectHistoryAddress = (address: IAddressWithCoordinate) => {
    dispatch(AddressActions.setCurrentAddress(address));
    setStepAddressDropdown(EnumStepAddressDropdown.empty);
  };

  return (
    <Container data-testid={ADDRESS_DROPDOWN_TEST_ID} ref={containerEl} className={className} onKeyDown={onKeyDown}>
      <Input
        onFocus={handleFocus}
        name="address_dropdown_desktop"
        onChange={onChangeAddress}
        value={enteredAddress}
        error={errorMsg}
        testId={ADDRESS_DROPDOWN_INPUT_TEST_ID}
        errorType={EnumInputErrorTypes.tooltip}
        ref={inputRef}
        {...{ use, size, placeholder, after, before, autoFocus }}
      />
      {
        {
          [EnumStepAddressDropdown.list]: (
            <AddressDropdownList list={list} onSelectAddress={handleSelectAddress} hoveredIndex={hoveredIndex} />
          ),
          [EnumStepAddressDropdown.history]: <SearchHistory onSelectHistoryAddress={handleSelectHistoryAddress} />,
          [EnumStepAddressDropdown.empty]: null,
        }[stepAddressDropdown]
      }
    </Container>
  );
};

export default AddressDropdown;
